import {ChangeEvent, FC, FormEvent, InputHTMLAttributes, Ref, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {IFormFieldProps} from '../../IFormFieldProps';
import FieldErrorMessage from '../messages/FieldErrorMessage';
import {useAppDispatch, useAppSelector} from '../../../../store/hook';
import Element from '../../../../domain/Element/Element';
import SaveUseCase from '../../../../domain/Form/SaveUseCase';
import FormGateway from '../../../../gateway/Form/FormGateway';
import getElementsIdToRender from '../../../util/getElementsIdToRender';
import {updateElementTimestamp} from '../../../../store/element/elements';
import parse from "html-react-parser";
import {
    computeCaretPosition,
    formatNumberForDisplay,
    normalizeFromDisplay
} from '../../../util/numberFormatter';

interface IProps extends InputHTMLAttributes<HTMLInputElement>, IFormFieldProps {
    id?: string
    classes: string,
    value?: string,
    defaultValue?: string | number
    onChange?: (e: FormEvent<HTMLInputElement>) => void,
    clearErrors: (id: string | undefined) => void
    groupSeparator?: string
    decimalSeparator?: string
}

const CurrencyAmountInput: FC<IProps> = (
    {
        id,
        classes,
        label,
        onChange,
        value,
        defaultValue,
        register,
        clearErrors,
        error,
        required,
        groupSeparator,
        decimalSeparator,
        ...rest
    }
) => {
    const dispatch = useAppDispatch()

    const elementsWithConditionJSON = useAppSelector(state => state.elements.currentElementsWithCondition)
    const elementsWithCondition: Element[] = JSON.parse(elementsWithConditionJSON)
    const elementsWithCalculeJSON = useAppSelector(state => state.elements.currentElementsWithCalcule)
    const elementsWithCalcule: Element[] = JSON.parse(elementsWithCalculeJSON)
    const elementsWithReferenceJSON = useAppSelector(state => state.elements.currentElementsWithReference)
    const elementsWithReference: Element[] = JSON.parse(elementsWithReferenceJSON)

    const useFormatted = groupSeparator !== undefined
        || (decimalSeparator !== undefined && decimalSeparator !== '.')

    const separatorOptions = {groupSeparator, decimalSeparator}

    const initialRaw = (() => {
        const source = value !== undefined
            ? value
            : (defaultValue !== undefined ? String(defaultValue) : '')
        return source
    })()

    const [displayValue, setDisplayValue] = useState<string>(() => {
        if (!useFormatted) return ''
        return formatNumberForDisplay(initialRaw, separatorOptions)
    })
    const [rawValue, setRawValue] = useState<string>(() => {
        if (!useFormatted) return ''
        return initialRaw
    })

    const inputRef = useRef<HTMLInputElement | null>(null)
    const caretRef = useRef<number | null>(null)

    useEffect(() => {
        if (!useFormatted) return
        const source = value !== undefined
            ? value
            : (defaultValue !== undefined ? String(defaultValue) : '')
        setDisplayValue(formatNumberForDisplay(source, separatorOptions))
        setRawValue(source)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, defaultValue, groupSeparator, decimalSeparator])

    useLayoutEffect(() => {
        if (!useFormatted) return
        if (caretRef.current !== null && inputRef.current) {
            try {
                inputRef.current.setSelectionRange(caretRef.current, caretRef.current)
            } catch (e) {
                // selection not supported on this input type
            }
            caretRef.current = null
        }
    }, [displayValue, useFormatted])

    const handleChange = (element) => {
        clearErrors(id)

        if (undefined !== onChange) {
            onChange(element)
        } else {
            if (undefined !== id) {
                const data = [];
                data[id] = element.currentTarget.value;

                const saveUseCase = new SaveUseCase(new FormGateway())
                saveUseCase.execute(data).then(() => {
                    const elementsIdToRender: string[] = getElementsIdToRender(elementsWithCondition, id, elementsWithCalcule, elementsWithReference)
                    elementsIdToRender.map(elementIdToRender => dispatch(updateElementTimestamp({'id': elementIdToRender})))
                })
            }
        }
    };

    if (undefined !== defaultValue && undefined !== id) {
        const response = new FormGateway().getCurrentValueForFormId()

        if (undefined === response.values[id]) {
            const data = [];
            data[id] = defaultValue;

            const saveUseCase = new SaveUseCase(new FormGateway())
            saveUseCase.execute(data).then(r => r)
        }
    }

    if (useFormatted) {
        const filterNumeric = (s: string): string => {
            const negative = s.startsWith('-')
            let body = negative ? s.slice(1) : s
            body = body.replace(/[^0-9.]/g, '')
            const firstDot = body.indexOf('.')
            if (firstDot !== -1) {
                body = body.slice(0, firstDot + 1) + body.slice(firstDot + 1).replace(/\./g, '')
            }
            return negative ? '-' + body : body
        }

        const onFormattedChange = (e: ChangeEvent<HTMLInputElement>) => {
            const el = e.currentTarget
            const rawInput = el.value
            const oldCaret = el.selectionStart ?? rawInput.length

            const stripped = normalizeFromDisplay(rawInput, separatorOptions)
            const cleaned = filterNumeric(stripped)
            const newDisplay = formatNumberForDisplay(cleaned, separatorOptions)

            caretRef.current = computeCaretPosition(rawInput, newDisplay, oldCaret, separatorOptions)
            setDisplayValue(newDisplay)
            setRawValue(cleaned)

            const synthetic = {
                ...e,
                target: {...el, value: cleaned},
                currentTarget: {...el, value: cleaned}
            } as unknown as FormEvent<HTMLInputElement>
            handleChange(synthetic)
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {type: _t, step: _s, ...inputRest} = rest

        return (
            <div className={`${classes} ${'currency-amount'} ${rest.readOnly ? 'readonly-element' : ''}`}>
                <div className={`form-floating ${error ? 'error' : ''} `}>
                    <input
                        type="hidden"
                        name={id}
                        ref={register as unknown as Ref<HTMLInputElement>}
                        value={rawValue}
                        readOnly
                    />
                    <input
                        className="form-control"
                        id={id}
                        type="text"
                        inputMode="decimal"
                        value={displayValue}
                        {...inputRest}
                        readOnly={rest.readOnly}
                        required={required}
                        ref={inputRef}
                        onChange={onFormattedChange}
                        title={rest.help}
                    />
                    <label htmlFor={id}>{parse(label ?? '')}</label>
                    {error && <FieldErrorMessage type={error.type} message={error.message}/>}
                </div>
            </div>
        )
    }

    return (

        <div className={`${classes} ${'currency-amount'} ${rest.readOnly ? 'readonly-element' : ''}`}>
            <div className={`form-floating ${error ? 'error' : ''} `}>
                <input
                    className="form-control"
                    value={value}
                    defaultValue={defaultValue}
                    {...rest}
                    readOnly={rest.readOnly}
                    required={required}
                    ref={register}
                    onChange={handleChange}
                    name={id}
                    title={rest.help}
                />
                <label htmlFor={id}>{parse(label ?? '')}</label>
                {error && <FieldErrorMessage type={error.type} message={error.message}/>}
            </div>
        </div>

    )
};

export default CurrencyAmountInput;
